import React, { useMemo, useState } from 'react'
import ReportChartRenderer from '../components/ReportChartRenderer'
import DrillDownCell from '../components/DrillDownCell'
import { StepHeader } from '../components/StepLayout'

function formatCellValue(col, val) {
  if (val === null || val === undefined || val === '') return '—'
  if (col.dataType === 'currency') {
    const symbol = col.format === 'currency_usd' ? '$' : '₹'
    return `${symbol}${Number(val || 0).toLocaleString()}`
  }
  if (col.dataType === 'percentage') return `${val}%`
  return String(val)
}

function sumColumn(rows, key) {
  return rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)
}

// Rows arrive already sorted by group field (the query builder orders by
// grouping fields first), so a single sequential pass is enough to bucket
// consecutive rows into their group without re-sorting on the client.
function buildGroupTree(rows, groupFields, level = 0) {
  if (level >= groupFields.length) return { type: 'leaf', rows }
  const field = groupFields[level].field
  const buckets = []
  let current = null
  for (const row of rows) {
    const val = row[field]
    if (!current || current.value !== val) {
      current = { value: val, rows: [] }
      buckets.push(current)
    }
    current.rows.push(row)
  }
  return {
    type: 'groups',
    field,
    showSubtotals: groupFields[level].showSubtotals,
    groups: buckets.map((b) => ({
      value: b.value,
      count: b.rows.length,
      node: buildGroupTree(b.rows, groupFields, level + 1),
    })),
  }
}

function flattenLeafRows(node) {
  if (node.type === 'leaf') return node.rows
  return node.groups.flatMap((g) => flattenLeafRows(g.node))
}

function downloadRowsAsCsv(columns, rows, filename) {
  const escape = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const header = columns.map((c) => escape(c.label || c.key)).join(',')
  const body = rows.map((r) => columns.map((c) => escape(r[c.key])).join(',')).join('\n')
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function GroupNode({ node, columns, level }) {
  if (node.type === 'leaf') {
    return (
      <>
        {node.rows.map((row, idx) => (
          <tr key={idx} className="hover:bg-surface-container-lowest transition-colors">
            <td className="py-2 px-3 text-slate-400 text-[11px] font-mono" style={{ paddingLeft: `${12 + level * 16}px` }}>
              {level > 0 ? '•' : idx + 1}
            </td>
            {columns.map((col) => (
              <td key={col.key} className="py-2 px-3 text-on-surface">
                {col.drillDownType ? (
                  <DrillDownCell value={row[col.key]} drillDownType={col.drillDownType} row={row} />
                ) : (
                  formatCellValue(col, row[col.key])
                )}
              </td>
            ))}
          </tr>
        ))}
      </>
    )
  }

  return (
    <>
      {node.groups.map((g, gi) => (
        <React.Fragment key={gi}>
          <tr className="bg-surface-container-low/80">
            <td
              colSpan={columns.length + 1}
              className="py-1.5 px-3 text-[11px] font-bold text-primary"
              style={{ paddingLeft: `${12 + level * 16}px` }}
            >
              {g.value || g.value === 0 ? String(g.value) : 'Unassigned'}
              <span className="ml-2 text-on-surface-variant font-medium">
                ({g.count} {g.count === 1 ? 'record' : 'records'})
              </span>
            </td>
          </tr>
          <GroupNode node={g.node} columns={columns} level={level + 1} />
          {node.showSubtotals && (
            <tr className="bg-primary/5 border-y border-primary/10">
              <td
                className="py-1.5 px-3 text-[10px] font-bold text-primary uppercase tracking-wider"
                style={{ paddingLeft: `${12 + (level + 1) * 16}px` }}
              >
                Subtotal
              </td>
              {columns.map((col) => {
                const isNumeric = col.dataType === 'currency' || col.dataType === 'number'
                if (!isNumeric) return <td key={col.key} className="py-1.5 px-3" />
                const flat = flattenLeafRows(g.node)
                return (
                  <td key={col.key} className="py-1.5 px-3 text-[11px] font-bold text-primary">
                    {formatCellValue(col, sumColumn(flat, col.key))}
                  </td>
                )
              })}
            </tr>
          )}
        </React.Fragment>
      ))}
    </>
  )
}

export default function Step9Preview({
  previewData,
  previewLoading,
  previewError,
  fetchPreview,
  visualization,
  grouping = [],
  filterConditions = [],
  filterLogic = 'AND',
  sorting = [],
  allFields = [],
}) {
  const [viewTab, setViewTab] = useState('both')

  const columns = previewData?.columns || []
  const rows = previewData?.rows || []
  const totalRows = previewData?.summary?.totalRows ?? 0
  const cards = previewData?.cards || []
  const chartData = previewData?.chartData || []
  const hasChart = Boolean(visualization?.type) && visualization.type !== 'TABLE'
  const effectiveTab = hasChart ? viewTab : 'table'

  const fieldLabelOf = useMemo(() => {
    return (key) => {
      const fromAll = allFields.find((f) => f.key === key)
      if (fromAll) return fromAll.label
      const fromCols = columns.find((c) => c.key === key)
      if (fromCols) return fromCols.label
      return key
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFields, columns])

  const groupTree = useMemo(() => {
    if (!grouping.length || rows.length === 0) return null
    return buildGroupTree(rows, grouping)
  }, [grouping, rows])

  const contextChips = useMemo(() => {
    const chips = []
    if (filterConditions.length > 0) {
      chips.push({
        icon: 'filter_list',
        text: `${filterConditions.length} filter${filterConditions.length > 1 ? 's' : ''} (${filterLogic})`,
      })
    }
    if (grouping.length > 0) {
      chips.push({ icon: 'folder_open', text: `Grouped by ${grouping.map((g) => fieldLabelOf(g.field)).join(' → ')}` })
    }
    if (sorting.length > 0) {
      chips.push({ icon: 'sort', text: `Sorted by ${sorting.map((s) => fieldLabelOf(s.field)).join(', ')}` })
    }
    return chips
  }, [filterConditions, filterLogic, grouping, sorting, fieldLabelOf])

  if (previewLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-3">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-xs font-semibold text-on-surface-variant">Executing live query compile and preview...</p>
      </div>
    )
  }

  if (previewError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[380px] py-12 px-4 text-center">
        <div className="flex flex-col items-center justify-center max-w-md w-full p-8 rounded-[12px] border border-red-200/80 bg-red-50/60 shadow-xs space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <span className="material-symbols-outlined text-3xl">error_outline</span>
          </div>

          <div className="space-y-1.5">
            <h4 className="text-base md:text-lg font-bold text-on-surface">Query Compilation Error</h4>
            <p className="text-sm text-on-surface-variant leading-relaxed">{previewError}</p>
          </div>

          <button
            type="button"
            onClick={fetchPreview}
            className="h-10 px-5 text-sm font-semibold bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-[8px] flex items-center gap-2 shadow-xs transition-colors"
          >
            <span className="material-symbols-outlined text-lg text-white">refresh</span>
            <span className="text-white">Retry Preview</span>
          </button>
        </div>
      </div>
    )
  }

  if (!previewData) {
    return (
      <div className="text-center py-16">
        <button
          type="button"
          onClick={fetchPreview}
          className="h-10 px-6 bg-primary text-on-primary rounded-[8px] text-xs font-bold shadow hover:opacity-90"
        >
          Run Live Preview
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3 max-w-5xl mx-auto py-1">
      <StepHeader
        title="Live Interactive Preview"
        subtitle="Inspect real query output, metric totals, and chart rendering with your active configuration."
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold bg-surface-container-high text-primary px-3 py-1.5 rounded-[8px]">
              Total Matching: {totalRows.toLocaleString()} rows
            </span>
            <button
              type="button"
              onClick={fetchPreview}
              className="p-1.5 rounded-[8px] border border-surface-container-high hover:bg-surface-container text-slate-500"
              title="Refresh Preview"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
            </button>
          </div>
        }
      />

      {/* Context summary chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {contextChips.length === 0 ? (
          <span className="text-[11px] text-on-surface-variant italic">
            No filters, grouping, or sorting applied — showing all accessible records.
          </span>
        ) : (
          contextChips.map((chip, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2.5 py-1 text-[11px] font-semibold text-on-surface-variant"
            >
              <span className="material-symbols-outlined text-[13px]">{chip.icon}</span>
              {chip.text}
            </span>
          ))
        )}
      </div>

      {/* Metric Cards */}
      {cards.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {cards.map((c) => (
            <div key={c.key} className="p-2.5 bg-surface-container-low rounded-[8px] border border-surface-container">
              <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider block truncate">
                {c.label}
              </span>
              <span className="text-sm font-bold text-primary mt-0.5 block">
                {c.format === 'currency' ? `₹${Number(c.value).toLocaleString()}` : c.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* View tabs + export */}
      <div className="flex items-center justify-between">
        {hasChart ? (
          <div className="inline-flex items-center gap-0.5 bg-surface-container-high p-0.5 rounded-[8px]">
            {[
              { id: 'table', label: 'Table' },
              { id: 'chart', label: 'Chart' },
              { id: 'both', label: 'Both' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setViewTab(tab.id)}
                className={`px-3 py-1 rounded text-xs font-bold transition-colors ${
                  effectiveTab === tab.id ? 'bg-primary text-on-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        {rows.length > 0 && (
          <button
            type="button"
            onClick={() => downloadRowsAsCsv(columns, rows, 'report_preview.csv')}
            className="flex items-center gap-1.5 text-xs font-bold text-on-surface-variant hover:text-primary"
            title="Download the rows currently shown in this preview"
          >
            <span className="material-symbols-outlined text-sm">download</span>
            Export shown rows
          </button>
        )}
      </div>

      {/* Chart Preview */}
      {(effectiveTab === 'chart' || effectiveTab === 'both') && (
        <ReportChartRenderer visualization={visualization} chartData={chartData} summaryCards={cards} columns={columns} />
      )}

      {/* Tabular Data Grid */}
      {(effectiveTab === 'table' || effectiveTab === 'both') && (
        <div className="bg-surface rounded-[10px] border border-surface-container overflow-hidden shadow-xs">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 p-4 text-center space-y-1.5">
              <span className="material-symbols-outlined text-3xl text-slate-300">search_off</span>
              <h4 className="text-xs font-bold text-on-surface">No records match the selected criteria.</h4>
              <p className="text-[10px] text-on-surface-variant max-w-sm">
                Try adjusting filter values, expanding the date range, or verifying company and branch scope assignments.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[360px]">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-surface-container-low sticky top-0 z-10 border-b border-surface-container">
                  <tr>
                    <th className="py-2.5 px-3 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider w-10">
                      #
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className="py-2.5 px-3 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container-low">
                  {groupTree ? (
                    <GroupNode node={groupTree} columns={columns} level={0} />
                  ) : (
                    rows.map((row, rIdx) => (
                      <tr key={rIdx} className="hover:bg-surface-container-lowest transition-colors">
                        <td className="py-2 px-3 text-slate-400 text-[11px] font-mono">{rIdx + 1}</td>
                        {columns.map((col) => (
                          <td key={col.key} className="py-2 px-3 text-on-surface">
                            {col.drillDownType ? (
                              <DrillDownCell value={row[col.key]} drillDownType={col.drillDownType} row={row} />
                            ) : (
                              formatCellValue(col, row[col.key])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-on-surface-variant text-right">
        Showing first {rows.length} rows for preview. Full report will process all records upon run/export.
      </p>
    </div>
  )
}
